import type { Count } from '../lib/format'

import type { Translations } from './types'

/**
 * Thai. Typed against the Russian source: every key and every function
 * signature must match. Thai nouns do not change after a numeral
 * (« 1 สัญญาณ », « 5 สัญญาณ »), so no plural helper is needed. Amounts and
 * dates arrive already formatted; the dictionary supplies only the words
 * around them. Thai is written without spaces inside a phrase, so a space is
 * used only where a figure or a Latin word meets Thai text.
 */
export const th: Translations = {
  common: {
    all: 'สูงสุด',
    back: 'กลับ',
    cancel: 'ยกเลิก',
    confirm: 'ยืนยัน',
    copy: 'คัดลอก',
    copied: 'คัดลอกแล้ว',
    open: 'เปิด',
    retry: 'ลองใหม่',
    save: 'บันทึก',
    close: 'ปิด',
    loading: 'กำลังโหลด',
    available: 'ใช้ได้',
    total: 'รวม',
    amount: 'จำนวน',
    network: 'เครือข่าย',
    asset: 'สินทรัพย์',
    status: 'สถานะ',
    date: 'วันที่',
    nothingYet: 'ยังไม่มีข้อมูล',
    somethingWentWrong: 'เกิดข้อผิดพลาด',
    reload: 'โหลดใหม่',
    crashHint: 'ไม่สามารถแสดงหน้านี้ได้ โปรดโหลดแอปใหม่ — เงินและคำขอของคุณไม่ได้รับผลกระทบ',
  },

  nav: {
    label: 'เมนูหลัก',
    home: 'หน้าแรก',
    markets: 'ตลาด',
    signals: 'สัญญาณ',
    trade: 'เทรด',
    wallet: 'กระเป๋าเงิน',
  },

  auth: {
    signIn: 'เข้าสู่ระบบ',
    signInSubtitle: 'เข้าสู่ระบบเพื่อกลับไปยังสัญญาณและยอดเงินของคุณ',
    signInAction: 'เข้าสู่ระบบ',
    signUp: 'สมัครสมาชิก',
    signUpSubtitle: 'ใช้เพียงอีเมลและรหัสผ่านก็เริ่มได้',
    signUpSubtitleInvite: 'ใช้เพียงอีเมล รหัสผ่าน และรหัสเชิญก็เริ่มได้',
    email: 'อีเมล',
    emailPlaceholder: 'name@example.com',
    password: 'รหัสผ่าน',
    passwordHint: 'อย่างน้อย 8 ตัวอักษร ประกอบด้วยตัวอักษรและตัวเลข',
    passwordConfirm: 'ยืนยันรหัสผ่าน',
    referralCode: 'รหัสเชิญ',
    referralCodePlaceholder: 'ตัวอย่างเช่น 8K3M7QPL',
    referralCodeHint: 'ไม่บังคับ — กรอกหากมีสมาชิก 19CRT เชิญคุณมา',
    referralCodeRequiredHint: 'การสมัครต้องมีคำเชิญเท่านั้น: ใช้ลิงก์หรือรหัสของผู้ที่เชิญคุณ',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `สัญญาณโบนัส ${signals.text} รายการ` : null,
        promo === null ? null : `โบนัสโปรโมชัน ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `หากสมาชิก 19CRT ให้รหัสนี้กับคุณ คำเชิญจะมีผลหลังฝากเงินครั้งแรกอย่างน้อย ${deposit} ${asset}`
        : `หากสมาชิก 19CRT ให้รหัสนี้กับคุณ คุณจะได้รับ${perks.join(' และ ')}หลังฝากเงินครั้งแรกอย่างน้อย ${deposit} ${asset}`
    },
    forgot: 'ลืมรหัสผ่าน?',
    createAccount: 'สร้างบัญชี',
    haveAccount: 'มีบัญชีอยู่แล้ว?',
    noAccount: 'ยังไม่มีบัญชี?',
    backToSignIn: 'กลับไปหน้าเข้าสู่ระบบ',
    verifyTitle: 'ยืนยันอีเมลของคุณ',
    verifySent: (email: string) => `เราส่งลิงก์ไปที่ ${email} แล้ว`,
    verifySentAnywhere: 'เราส่งลิงก์ไปที่อีเมลของคุณแล้ว',
    verifyHint:
      'เปิดลิงก์จากอีเมลเพื่อยืนยันที่อยู่ เพราะอีเมลกู้คืนรหัสผ่านจะถูกส่งไปที่นั่น หากยังไม่ได้รับ โปรดตรวจดูในกล่องจดหมายขยะ',
    verifyEmailPrompt: 'กรอกอีเมลที่ใช้สมัครบัญชี',
    verifyResend: 'ส่งอีเมลอีกครั้ง',
    verifyResendIn: (seconds: Count) => `ส่งใหม่ได้ในอีก ${seconds.text} วินาที`,
    verifyResent: 'กำลังส่งอีเมลอีกฉบับ',
    verifyResentMaybe: 'หากอีเมลนี้ลงทะเบียนไว้และยังไม่ได้ยืนยัน ระบบกำลังส่งอีเมลฉบับใหม่',
    verifyChecking: 'กำลังตรวจสอบลิงก์',
    verifyDone: 'ยืนยันอีเมลแล้ว',
    verifyDoneHint: 'กำลังเปิดแอป',
    verifyDoneSignInHint: 'กำลังเปิดหน้าเข้าสู่ระบบ',
    resetTitle: 'ตั้งรหัสผ่านใหม่',
    resetSubtitle: 'กรอกอีเมลของคุณ แล้วเราจะส่งลิงก์สำหรับเปลี่ยนรหัสผ่านไปให้',
    resetSend: 'ส่งลิงก์',
    resetSent: 'หากมีบัญชีนี้อยู่ ระบบกำลังส่งอีเมลไปให้',
    resetSentHint:
      'เราไม่เปิดเผยว่าอีเมลใดลงทะเบียนไว้ — มิฉะนั้นแบบฟอร์มนี้จะถูกใช้สืบหาอีเมลของผู้อื่นได้ โปรดตรวจดูกล่องจดหมายและกล่องจดหมายขยะ',
    newPassword: 'รหัสผ่านใหม่',
    newPasswordSubtitle: 'ตั้งรหัสผ่านใหม่ รหัสผ่านเดิมจะใช้ไม่ได้ทันที',
    newPasswordSubmit: 'บันทึกรหัสผ่าน',
    requestNewLink: 'ขอลิงก์ใหม่',
    newPasswordDone: 'เปลี่ยนรหัสผ่านแล้ว',
    newPasswordDoneHint: 'กำลังเปิดหน้าเข้าสู่ระบบ',
    signOut: 'ออกจากระบบ',
    totpCode: 'รหัสยืนยัน',
    totpHint: 'ตัวเลขหกหลักจากแอปยืนยันตัวตน',
    recoveryCode: 'รหัสกู้คืน',
    recoveryCodeHint: 'อักขระแปดตัวจากชุดรหัสที่คุณบันทึกไว้ตอนเปิดใช้ 2FA',
    useRecoveryCode: 'ใช้รหัสกู้คืน',
    useAppCode: 'ใช้แอปยืนยันตัวตน',
    resetLinkInvalid: 'ลิงก์นี้ใช้ไม่ได้',
    errors: {
      invalidCredentials: 'อีเมลหรือรหัสผ่านไม่ถูกต้อง',
      emailTaken: 'อีเมลนี้ถูกใช้ลงทะเบียนแล้ว',
      weakPassword: 'รหัสผ่านสั้นเกินไป',
      passwordRule: 'รหัสผ่านต้องมีทั้งตัวอักษรและตัวเลข',
      passwordsDiffer: 'รหัสผ่านไม่ตรงกัน',
      invalidEmail: 'กรอกอีเมลในรูปแบบ name@example.com',
      invalidReferralCode: 'โปรดตรวจสอบรหัสเชิญ',
      referralRequired: 'กรอกรหัสเชิญของคุณ',
      emailRequired: 'กรอกอีเมลของคุณ',
      passwordRequired: 'กรอกรหัสผ่านของคุณ',
      accountBlocked: 'บัญชีนี้ถูกจำกัดการใช้งาน โปรดติดต่อฝ่ายสนับสนุน',
      rateLimited: 'พยายามมากเกินไป โปรดลองใหม่ภายหลัง',
      totpRequired: 'กรอกรหัสจากแอปยืนยันตัวตน',
      totpInvalid: 'รหัสไม่ถูกต้อง โปรดตรวจเวลาบนโทรศัพท์แล้วลองรหัสใหม่',
      totpFormat: 'รหัสจากแอปมีหกหลัก ส่วนรหัสกู้คืนมีแปดอักขระ',
      expiredToken: 'ลิงก์นี้หมดอายุแล้ว โปรดขอลิงก์ใหม่',
      verifyLinkUsed: 'ลิงก์นี้ถูกใช้ไปแล้ว โปรดเข้าสู่ระบบ — หรือเปิดอีเมลฉบับล่าสุดหากคุณขอลิงก์ใหม่',
      missingToken: 'ลิงก์ไม่สมบูรณ์ โปรดเปิดลิงก์เต็มจากอีเมล',
      validation: 'โปรดตรวจสอบข้อมูลที่กรอก',
      network: 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ โปรดตรวจสอบเครือข่ายของคุณ',
    },
  },

  home: {
    promoLabel: 'โปรโมชัน',
    banners: [
      {
        title: 'สัญญาณทองคำ',
        subtitle: 'รหัสจากแชนเนล Telegram ของเรา — เทรดได้ในขั้นตอนเดียว',
        cta: 'กรอกรหัส',
      },
      {
        title: 'ตลาดแบบเรียลไทม์',
        subtitle: 'ราคา กราฟ และสมุดคำสั่งของทุกคู่เทรดแบบสด',
        cta: 'ดูตลาด',
      },
      {
        title: 'เติมเงินเข้าบัญชีในไม่กี่นาที',
        subtitle: 'ฝาก USDT ผ่าน TRC20 หรือ ERC20',
        cta: 'ฝากเงิน',
      },
    ],
    pauseAnnouncements: 'หยุดประกาศชั่วคราว',
    resumeAnnouncements: 'เล่นประกาศต่อ',
    announcementsLabel: 'ประกาศ',
    announcements: [
      'ตรวจสอบสินทรัพย์และเครือข่ายก่อนฝากเงินทุกครั้ง',
      'ตั้งค่าการป้องกันบัญชีได้ในหน้าความปลอดภัย',
      'ดูประวัติธุรกรรมของคุณได้ในหน้าสินทรัพย์',
    ],
    totalBalance: 'ยอดรวมทั้งหมด',
    quickTrade: 'เทรดด่วน',
    quickTradeHint: 'สปอตและฟิวเจอร์สในหน้าเดียว',
    quickActions: 'ทางลัด',
    signalLive: 'มีสัญญาณพร้อมใช้',
    signalNext: 'สัญญาณถัดไป',
    signalClosesIn: 'ปิดในอีก',
    signalOpensAt: (time: string) => `เปิดเวลา ${time}`,
    signalsTitle: 'สัญญาณทองคำ',
    allSignals: 'สัญญาณทั้งหมด',
    marketMoves: 'ความเคลื่อนไหวของตลาด',
    pair: 'คู่เทรด',
    lastPrice: 'ราคา',
    change24h: 'เปลี่ยนแปลง 24 ชม.',
    /** After the figure in the home balance's change pill: « +1.30% 24h ». */
    change24hShort: '24 ชม.',
    volume24h: 'ปริมาณ 24 ชม.',
    allMarkets: 'ตลาดทั้งหมด',
    pricesUnavailable: 'ไม่มีข้อมูลราคา — ไม่สามารถตีมูลค่าพอร์ตได้ครบถ้วน',
    ratesUnavailable: 'ข้อมูลราคาไม่พร้อมใช้งานชั่วคราว',
    marketsUnavailable: 'รายการตลาดไม่พร้อมใช้งานชั่วคราว',
    signalsNone: 'ยังไม่มีสัญญาณในขณะนี้ — รหัสใหม่จะประกาศในแชนเนล Telegram ของเรา',
  },

  markets: {
    title: 'ตลาด',
    search: 'ค้นหาคู่เทรด',
    spot: 'สปอต',
    futures: 'ฟิวเจอร์ส',
    gainers: 'ขึ้นมากสุด',
    losers: 'ลงมากสุด',
    volume: 'ปริมาณ 24 ชม.',
    volumeShort: 'ปริมาณ',
    typeLabel: 'ประเภทตลาด',
    sortLabel: 'การเรียงลำดับ',
    favourites: 'รายการโปรด',
    addFavourite: 'เพิ่มในรายการโปรด',
    removeFavourite: 'นำออกจากรายการโปรด',
    emptyFavourites: 'ยังไม่มีรายการโปรด — กดดาวที่คู่เทรดที่คุณติดตาม',
    clearSearch: 'ล้างคำค้นหา',
    empty: 'ไม่พบรายการที่ตรงกัน',
    emptyHint: 'ลองตรวจสอบสัญลักษณ์คู่เทรด เช่น BTC หรือ SOL',
    emptySpot: 'ยังไม่มีคู่เทรดสปอตที่เปิดอยู่',
    emptyFutures: 'ยังไม่มีคู่เทรดฟิวเจอร์สที่เปิดอยู่',
    loadFailed: 'โหลดรายการตลาดไม่สำเร็จ',
    pricesUnavailable: 'ข้อมูลราคาไม่พร้อมใช้งานชั่วคราว ระบบจะอัปเดตให้เอง',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'ทองคำ' } as Record<string, string>,
  },

  wallet: {
    title: 'กระเป๋าเงิน',
    totalValue: 'มูลค่าบัญชีทั้งหมด',
    accounts: 'บัญชีของฉัน',
    latestWithdrawal: 'การถอนล่าสุด',
    coin: 'เหรียญ',
    searchCoin: 'ค้นหาเหรียญ',
    quickActions: 'ทางลัด',
    coins: 'เหรียญ',
    hideBalance: 'ซ่อนยอดเงิน',
    showBalance: 'แสดงยอดเงิน',
    loadError: 'โหลดยอดเงินของคุณไม่สำเร็จ',
    pricesUnavailable: 'ไม่มีข้อมูลราคา — ไม่สามารถตีมูลค่าพอร์ตได้ครบถ้วน',
    coinsEmpty: 'ยังไม่มีเหรียญ ฝากเงินแล้วเหรียญจะปรากฏที่นี่',
    accountSpot: 'บัญชีสปอต',
    accountSpotHint: 'พร้อมใช้เทรด',
    accountFutures: 'บัญชีฟิวเจอร์ส',
    accountFuturesHint: 'หลักประกันที่ค้ำสถานะอยู่',
    accountCopy: 'คัดลอกการเทรด (เก็บถาวร)',
    accountCopyHint: 'ย้ายไปบัญชีสปอต',
    actions: {
      deposit: 'ฝากเงิน',
      withdraw: 'ถอนเงิน',
      transfer: 'โอน',
      history: 'ประวัติ',
    },
    deposit: {
      title: 'ฝากเงิน',
      history: 'ประวัติการฝาก',
      chain: 'เครือข่าย',
      saveQr: 'บันทึกคิวอาร์โค้ด',
      address: 'ที่อยู่สำหรับฝากเงิน',
      copyAddress: 'คัดลอกที่อยู่',
      notice: 'ข้อควรระวัง',
      noticeWrongNetwork: (asset: string, network: string) =>
        `ส่งเฉพาะ ${asset} บนเครือข่าย ${network} เท่านั้น เหรียญที่ส่งผิดเครือข่ายจะสูญหาย`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `เข้าบัญชีหลังเครือข่ายยืนยัน ${count.text} ครั้ง โดยปกติใช้เวลาประมาณ ${minutes.text} นาที`,
      noticeMinimum: (amount: string, asset: string) =>
        `ฝากขั้นต่ำ ${amount} ${asset}`,
      waiting: 'กำลังรอการโอนของคุณ',
      incoming: 'เงินฝากที่กำลังเข้ามา',
      incomingFirst: 'พบบนเครือข่ายแล้ว กำลังรอการยืนยันครั้งแรก',
      incomingProgress: (done: Count, total: Count) => `การยืนยัน: ${done.text} จาก ${total.text}`,
      selectAsset: 'เลือกเหรียญ',
      noAssets: 'การฝากเงินไม่พร้อมใช้งานชั่วคราว โปรดลองใหม่ภายหลัง',
      noNetworks: 'ยังไม่มีเครือข่ายที่ใช้ได้กับเหรียญนี้ — โปรดเลือกเหรียญอื่น',
      addressFailed: 'ขอที่อยู่สำหรับฝากเงินไม่สำเร็จ',
      noticePermanent: 'ที่อยู่นี้เป็นที่อยู่ถาวร ใช้ได้กับการฝากเงินทุกครั้งในอนาคต',
      qrSaved: 'บันทึกคิวอาร์โค้ดแล้ว',
      qrFailed: 'บันทึกคิวอาร์โค้ดไม่สำเร็จ',
      paymentLink: 'ลิงก์สำหรับกระเป๋าเงิน',
      paymentLinkHint: 'เปิดลิงก์ในกระเป๋าเงินของคุณ — ระบบกรอกเครือข่ายและที่อยู่ให้แล้ว',
      copyLink: 'คัดลอกลิงก์',
    },
    withdraw: {
      title: 'ถอนเงิน',
      history: 'ประวัติการถอน',
      asset: 'สินทรัพย์ที่ถอน',
      selectAsset: 'เลือกสินทรัพย์',
      network: 'เครือข่ายที่ถอน',
      address: 'ที่อยู่ปลายทาง',
      addressPlaceholder: (network: string) => `กรอกที่อยู่บนเครือข่าย ${network}`,
      addressPaste: 'วางที่อยู่',
      addressFromBook: (label: string): string => `ยืนยันแล้วในสมุดที่อยู่ · ${label}`,
      paste: 'วาง',
      addressInvalid: (network: string) => `ที่อยู่นี้ดูไม่เหมือนที่อยู่บนเครือข่าย ${network}`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `นี่คือที่อยู่ของ ${detected} แต่คุณเลือก ${selected}`,
      amount: 'จำนวน',
      amountPlaceholder: '0.00',
      networkFee: 'ค่าธรรมเนียมเครือข่าย',
      platformFee: (rate: string): string => `ค่าธรรมเนียมแพลตฟอร์ม (${rate}%)`,
      feeFree: 'ยังไม่คิดค่าธรรมเนียม',
      feeDynamic: 'ตามราคาแก๊สปัจจุบัน',
      willReceive: 'คุณจะได้รับ',
      notice: 'ข้อควรระวัง',
      noticeReview: 'ทุกคำขอจะถูกตรวจสอบก่อนส่ง และระบบจะกันยอดเงินไว้ทันที',
      noticeTiming: 'ส่งภายใน 24 ชั่วโมงหลังอนุมัติ',
      noticeMinimum: (amount: string, asset: string) =>
        `ถอนขั้นต่ำ ${amount} ${asset}`,
      noticeSupport:
        'โปรดตรวจสอบที่อยู่ด้วยตัวเอง: การโอนไปยังที่อยู่ผิดไม่สามารถเรียกคืนได้ ฝ่ายสนับสนุนจะตรวจสอบข้อพิพาทตามหมายเลขคำขอ',
      insufficient: 'ยอดเงินไม่เพียงพอ',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `เหลือวันนี้: ${remaining} จาก ${limit} USDT` : `บัญชีที่ยังไม่ยืนยันตัวตนถอนได้สูงสุด ${limit} USDT ต่อวัน เหลืออีก ${remaining} ยืนยันตัวตนเพื่อเพิ่มวงเงิน`,
      addressNotConfirmed: 'ถอนได้เฉพาะไปยังที่อยู่ที่ยืนยันแล้วในสมุดที่อยู่เท่านั้น',
      limitReached: (verified: boolean): string => (verified ? 'ถึงวงเงินถอนต่อวันแล้ว' : 'ถึงวงเงินต่อวันของบัญชีที่ยังไม่ยืนยันตัวตนแล้ว — ยืนยันตัวตนในหน้าโปรไฟล์'),
      belowMinimum: (amount: string, asset: string) => `ขั้นต่ำ ${amount} ${asset}`,
      confirmTitle: 'ตรวจสอบคำขอ',
      confirmSubtitle: 'เมื่อส่งแล้วจะยกเลิกการถอนไม่ได้ โปรดตรวจสอบเครือข่ายและที่อยู่',
      confirmAction: 'ยืนยันการถอน',
      failed: 'สร้างคำขอไม่สำเร็จ',
      submitted: 'สร้างคำขอแล้ว',
      submittedHint: 'หลังตรวจสอบเสร็จ ระบบจะส่งเงินออกสู่เครือข่าย ติดตามสถานะได้ในรายการคำขอถอน',
      requestId: 'หมายเลขคำขอ',
      statusPending: 'กำลังตรวจสอบ',
      toRequests: 'ไปที่คำขอถอน',
      empty: 'ขณะนี้ยังไม่มีสินทรัพย์ที่ถอนได้',
      loadFailed: 'โหลดรายการสินทรัพย์ไม่สำเร็จ',
      routeUnavailable: 'ขณะนี้ถอนผ่านเครือข่ายนี้ไม่ได้ — โปรดเลือกเครือข่ายอื่น',
      invalidAmount: 'จำนวนนี้ใช้ไม่ได้: ตรวจสอบทศนิยมและค่าธรรมเนียมเครือข่าย',
      edit: 'แก้ไขคำขอ',
    },
    withdrawals: {
      title: 'คำขอถอนเงิน',
      toAddress: (address: string): string => `ไปยัง ${address}`,
      note: 'คำขอที่ถูกปฏิเสธหรือล้มเหลวจะคืนยอดที่กันไว้เข้าบัญชีของคุณ เปิดคำขอเพื่อดูธุรกรรมหรือเหตุผล',
      empty: 'ยังไม่มีคำขอถอนเงิน ทุกคำขอจะแสดงสถานะที่นี่ ตั้งแต่การตรวจสอบจนถึงการยืนยันบนเครือข่าย',
      loadError: 'โหลดคำขอของคุณไม่สำเร็จ',
      details: 'คำขอถอนเงิน',
      created: 'สร้างเมื่อ',
      transaction: 'ธุรกรรม',
      openExplorer: 'เปิดในบล็อกเอ็กซ์พลอเรอร์',
      reason: 'เหตุผล',
      status: {
        pending: 'กำลังตรวจสอบ',
        approved: 'อนุมัติแล้ว',
        rejected: 'ถูกปฏิเสธ',
        broadcasting: 'กำลังส่ง',
        sent: 'ส่งแล้ว',
        confirmed: 'ยืนยันแล้ว',
        failed: 'ล้มเหลว',
      },
      statusHint: {
        pending: 'กำลังตรวจสอบคำขอ ยอดเงินถูกกันไว้ในบัญชีของคุณ',
        approved: 'ตรวจสอบแล้ว กำลังรอส่งออกสู่เครือข่าย',
        rejected: 'คำขอถูกปฏิเสธ ยอดที่กันไว้กลับเข้าบัญชีของคุณแล้ว',
        broadcasting: 'กำลังส่งธุรกรรมออกสู่เครือข่าย',
        sent: 'ธุรกรรมอยู่บนเครือข่ายแล้ว กำลังรอการยืนยัน',
        confirmed: 'เครือข่ายยืนยันธุรกรรมแล้ว เงินถึงปลายทางเรียบร้อย',
        failed: 'ส่งไม่สำเร็จ ยอดที่กันไว้กลับเข้าบัญชีของคุณแล้ว',
      },
    },
    transfer: {
      title: 'โอนเงิน',
      from: 'จาก',
      fromAvailable: (amount: string): string => `จาก · ใช้ได้ ${amount}`,
      to: 'ไปยัง',
      submit: 'โอน',
      swap: 'สลับบัญชีทั้งสอง',
      selectFrom: 'บัญชีต้นทาง',
      selectTo: 'บัญชีปลายทาง',
      selectAsset: 'เลือกเหรียญ',
      after: 'หลังการโอน',
      insufficient: 'ยอดเงินในบัญชีต้นทางไม่เพียงพอ',
      done: 'โอนเรียบร้อยแล้ว',
      hint: 'การโอนระหว่างบัญชีของคุณเองทำได้ทันทีและไม่มีค่าธรรมเนียม',
      empty: 'ยังไม่มีอะไรให้โอน — ฝากเงินก่อน',
      failed: 'โอนไม่สำเร็จ โปรดลองใหม่',
    },
    history: {
      title: 'ประวัติธุรกรรม',
      deposit: 'ฝากเงิน',
      withdrawal: 'ถอนเงิน',
      trade: 'การเทรด',
      fee: 'ค่าธรรมเนียม',
      transfer: 'การโอน',
      pnl: 'ผลลัพธ์',
      empty: 'ยังไม่มีธุรกรรม การฝาก การถอน และการเทรดจะปรากฏที่นี่',
      emptyFiltered: 'ไม่มีธุรกรรมที่ตรงกับตัวกรองนี้',
      loadError: 'โหลดประวัติของคุณไม่สำเร็จ',
      today: 'วันนี้',
      yesterday: 'เมื่อวาน',
      balanceAfter: (amount: string) => `ยอดคงเหลือ ${amount}`,
      onlyAccount: (account: string) => `เฉพาะ${account}`,
      resetAccount: 'แสดงทุกบัญชี',
      accountFilter: 'บัญชี',
      allAccounts: 'ทุกบัญชี',
      withdrawalsLink: 'สถานะคำขอถอนเงิน',
      badges: {
        credited: 'เข้าบัญชีแล้ว',
        sent: 'ส่งแล้ว',
        fee: 'ค่าธรรมเนียม',
        reserved: 'กันไว้',
        returned: 'คืนแล้ว',
        released: 'ปลดแล้ว',
        filled: 'จับคู่แล้ว',
        settled: 'ชำระแล้ว',
        liquidated: 'ถูกบังคับปิด',
        done: 'เสร็จสิ้น',
        adjusted: 'ปรับปรุงแล้ว',
        paid: 'จ่ายแล้ว',
      },
      filters: {
        all: 'ทั้งหมด',
        rewards: 'รางวัล',
        deposits: 'การฝาก',
        withdrawals: 'การถอน',
        trades: 'การเทรด',
        transfers: 'การโอน',
      },
      types: {
        deposit: 'ฝากเงิน',
        withdrawal: 'ถอนเงิน',
        withdrawal_fee: 'ค่าธรรมเนียมการถอน',
        withdrawal_platform_fee: 'ค่าธรรมเนียมแพลตฟอร์มสำหรับการถอน',
        withdrawal_lock: 'กันยอดสำหรับการถอน',
        withdrawal_release: 'คืนยอดที่กันไว้สำหรับการถอน',
        order_lock: 'กันยอดสำหรับคำสั่งลิมิต',
        order_release: 'คืนยอดที่กันไว้สำหรับคำสั่ง',
        bonus: 'โบนัสโปรโมชัน',
        trade_buy: 'ซื้อ',
        trade_sell: 'ขาย',
        trading_fee: 'ค่าธรรมเนียมการเทรด',
        realized_pnl: 'ผลการเทรด',
        signal_programmed_profit: 'กำไรตามโปรแกรมของสัญญาณ',
        funding: 'ค่าฟันดิ้ง',
        margin_lock: 'กันหลักประกัน',
        margin_release: 'ปลดหลักประกัน',
        liquidation: 'การบังคับปิด',
        transfer_in: 'โอนเข้า',
        transfer_out: 'โอนออก',
        copy_allocation: 'จัดสรรไปคัดลอกการเทรด',
        copy_release: 'คืนจากการคัดลอกการเทรด',
        copy_commission: 'ค่าธรรมเนียมการคัดลอกการเทรด',
        referral: 'รางวัลแนะนำเพื่อน',
        adjustment: 'การปรับปรุงยอด',
      },
    },
  },

  trade: {
    title: 'เทรด',
    menu: {
      account: 'บัญชี',
      trading: 'การเทรด',
      more: 'เพิ่มเติม',
    },
    long: 'ลอง',
    short: 'ชอร์ต',
    buy: 'ซื้อ',
    sell: 'ขาย',
    marketOrder: 'ราคาตลาด',
    limitOrder: 'ลิมิต',
    price: 'ราคา',
    marketPrice: 'ที่ราคาตลาด',
    quantity: 'จำนวน',
    leverage: 'เลเวอเรจ',
    takeProfitStopLoss: 'ทำกำไร / ตัดขาดทุน',
    positions: 'สถานะ',
    orders: 'คำสั่ง',
    history: 'ประวัติ',
    copyTrading: 'คัดลอกการเทรด',
    unrealizedPnl: 'กำไรขาดทุนที่ยังไม่รับรู้',
    unrealizedPnlShort: 'กำไร/ขาดทุน',
    margin: 'หลักประกัน',
    entry: 'ราคาเข้า',
    size: 'ขนาด',
    liquidation: 'ราคาบังคับปิด',
    close: 'ปิด',
    orderBook: 'สมุดคำสั่ง',
    noPositions: 'ส่งคำสั่งด้านบน แล้วสถานะจะปรากฏที่นี่',
    balance: 'ยอดคงเหลือ',
    perpetual: 'ไม่มีกำหนดหมดอายุ',
    perpShort: 'เพิร์พ',
    fundingIn: (time: string) => `ในอีก ${time}`,
    position: 'สถานะ',
    closeAtMarket: 'ปิดที่ราคาตลาด',
    keepPosition: 'คงสถานะไว้',
    placeBuyOrder: 'ส่งคำสั่งซื้อ',
    placeSellOrder: 'ส่งคำสั่งขาย',
    placeLongOrder: 'ส่งคำสั่งลอง',
    placeShortOrder: 'ส่งคำสั่งชอร์ต',
    isolatedMargin: 'หลักประกันแยก',
    crossMargin: 'หลักประกันรวม',
    markShort: 'ราคามาร์ก',
    liqPrice: 'ราคาบังคับปิด',
    noPositionsTitle: 'ไม่มีสถานะที่เปิดอยู่',
    noOrdersTitle: 'ไม่มีคำสั่งที่รออยู่',
    noHistoryTitle: 'ยังไม่มีการเทรด',
    pair: 'คู่เทรด',
    selectPair: 'เลือกคู่เทรด',
    sideLabel: 'ทิศทาง',
    orderType: 'ประเภทคำสั่ง',
    sizeShare: 'สัดส่วนของยอดที่ใช้ได้',
    quantityShort: 'จำนวน',
    takeProfit: 'ทำกำไร',
    stopLoss: 'ตัดขาดทุน',
    tabsLabel: 'การเทรดของฉัน',
    openChart: 'กราฟ',
    openLong: (symbol: string) => `ลอง ${symbol}`,
    openShort: (symbol: string) => `ชอร์ต ${symbol}`,
    buyAsset: (symbol: string) => `ซื้อ ${symbol}`,
    sellAsset: (symbol: string) => `ขาย ${symbol}`,
    enterQuantity: 'กรอกจำนวน',
    insufficient: 'ยอดเงินในบัญชีนี้ไม่เพียงพอ',
    belowMinNotional: (amount: string, asset: string) =>
      `ขนาดคำสั่งขั้นต่ำคือ ${amount} ${asset}`,
    bookEmpty: 'ยังไม่มีคำสั่งรออยู่',
    bookUnavailable: 'สมุดคำสั่งไม่พร้อมใช้งาน',
    tradingUnavailable:
      'ระบบเทรดไม่ตอบสนอง จึงส่งคำสั่งไม่ได้ในขณะนี้ ยอดเงิน การฝากและการถอนไม่ได้รับผลกระทบ',
    marketsFailed: 'โหลดรายการคู่เทรดไม่สำเร็จ',
    noMarkets: 'ยังไม่มีคู่เทรด — จะปรากฏเมื่อเปิดตลาด',
    confirmTitle: 'ตรวจสอบคำสั่ง',
    confirmSubtitle: 'คำสั่งที่จับคู่แล้วไม่สามารถย้อนกลับได้',
    marginRequired: 'หลักประกันที่ใช้',
    cost: 'รวม',
    liquidationEstimate: 'ราคาบังคับปิดโดยประมาณ',
    submitted: 'ส่งคำสั่งแล้ว',
    submitFailed: 'ส่งคำสั่งไม่สำเร็จ',
    futuresSoon: 'ฟิวเจอร์สไม่พร้อมใช้งานชั่วคราว การเทรดสปอตยังใช้งานได้',
    marginMode: 'หลักประกัน',
    cross: 'รวม',
    isolated: 'แยก',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'แบบรวม: ยอดฟิวเจอร์สทั้งหมดค้ำสถานะนี้'
        : 'แบบแยก: เสี่ยงเฉพาะหลักประกันของสถานะนี้เท่านั้น',
    reduceOnly: 'ลดสถานะเท่านั้น',
    futuresAccount: 'บัญชีฟิวเจอร์ส',
    equity: 'มูลค่าสุทธิ',
    marginUsed: 'ใช้เป็นหลักประกัน',
    marginRatio: 'ระดับความเสี่ยง',
    funding: 'ฟันดิ้ง',
    fundingRate: 'อัตราทุก 8 ชม.',
    nextFunding: 'ครั้งถัดไป',
    fundingPaid: 'ฟันดิ้ง',
    protection: 'การป้องกัน',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'จุดทำกำไรและจุดตัดขาดทุน',
    protectionSheetSubtitle: 'ระบบจะปิดที่ราคาตลาดทันทีที่ราคาแตะระดับที่ตั้งไว้',
    clearLevel: 'ไม่ตั้งระดับ',
    addMargin: 'เพิ่มหลักประกัน',
    removeMargin: 'ถอนหลักประกัน',
    marginDelta: 'จำนวนหลักประกัน',
    marginDeltaHint: 'สถานะแบบแยก: หลักประกันมากขึ้นทำให้ราคาบังคับปิดห่างออกไป',
    addMarginKyc: 'การเพิ่มหลักประกันจะเปิดใช้เมื่อเอกสารของคุณได้รับอนุมัติ ตอนนี้คุณยังถอนหลักประกัน แก้ไขจุดทำกำไรและจุดตัดขาดทุน หรือปิดสถานะได้',
    saveProtection: 'บันทึก',
    protectionSaved: 'อัปเดตการป้องกันแล้ว',
    protectionFailed: 'อัปเดตสถานะไม่สำเร็จ',
    protectionInvalid: 'ระดับที่ตั้งอยู่ผิดฝั่งของราคาเข้า',
    leverageTooHigh: 'เลเวอเรจเกินค่าสูงสุดของตลาดนี้',
    leverageTier: (cap: number, notional: string) => `สูงสุด ${cap}× สำหรับสถานะขนาด ${notional} USDT`,
    futuresDisabled: 'ขณะนี้ไม่รับคำสั่งฟิวเจอร์ส',
    liquidated: 'ถูกบังคับปิด',
    restingToast: (symbol: string, price: string) => `คำสั่งลิมิต ${symbol} ที่ราคา ${price} กำลังรอราคาอยู่`,
    cancelledToast: 'ยกเลิกคำสั่งแล้ว ปลดยอดที่กันไว้เรียบร้อย',
    costSell: 'คุณจะได้รับ',
    costBasis: 'ต้นทุน',
    enterProtection: 'ระดับทำกำไรหรือตัดขาดทุนไม่ใช่ราคาที่ถูกต้อง',
    leverageLocked: (leverage: number) => `เลเวอเรจถูกล็อกไว้ที่ ${leverage}× โดยสถานะที่เปิดอยู่ ปิดสถานะก่อนจึงจะเปลี่ยนได้`,
    marginReleased: 'ปลดหลักประกันแล้ว',
    realizedEstimate: 'ผลลัพธ์โดยประมาณ',
    addsPosition: (quantity: string, asset: string) => `เพิ่ม ${quantity} ${asset} เข้าสถานะที่เปิดอยู่`,
    reducesPosition: (quantity: string, asset: string) => `ลดสถานะที่เปิดอยู่ลง ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `ปิดสถานะ ${quantity} ${asset} ที่เปิดอยู่`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `ปิดสถานะ ${closeQuantity} ${asset} และเปิด ${openQuantity} ${asset} ในทิศทางตรงข้าม`,
    byAccount: 'ทั้งบัญชี',
    cancelledToastPlain: 'ยกเลิกคำสั่งแล้ว',
    fillBuy: 'ซื้อ',
    fillSell: 'ขาย',
    marginFloor: (floor: string) => `หลักประกันต้องไม่ต่ำกว่าหลักประกันเริ่มต้น ${floor} USDT`,
    marginRemovable: (amount: string) => `ถอนได้สูงสุด ${amount} USDT`,
    enterPrice: 'กรอกราคาลิมิต',
    markPrice: 'ราคามาร์ก',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'ข้อมูลราคาล้าสมัย — ฟีดราคาตามหลังอยู่ โปรดลองใหม่ในอีกไม่กี่วินาที',
    marketDisabled: 'การเทรดคู่นี้ถูกระงับชั่วคราว',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `${side} ${quantity} ${asset} ที่ราคา ${price} — จับคู่แล้ว`,
    closedToast: (symbol: string, pnl: string) => `ปิด ${symbol} แล้ว ผลลัพธ์ ${pnl} USDT`,
    positionsFailed: 'โหลดสถานะไม่สำเร็จ',
    noOrders: 'คำสั่งลิมิตจะรออยู่ที่นี่จนกว่าจะจับคู่',
    ordersFailed: 'โหลดคำสั่งไม่สำเร็จ',
    noHistory: 'การเทรดที่จับคู่สำเร็จครั้งแรกของคุณจะปรากฏในรายการนี้',
    historyFailed: 'โหลดประวัติการเทรดไม่สำเร็จ',
    cancelOrder: 'ยกเลิก',
    cancelFailed: 'ยกเลิกคำสั่งไม่สำเร็จ',
    closeFailed: 'ปิดสถานะไม่สำเร็จ',
    closeConfirmTitle: 'ปิดสถานะนี้หรือไม่?',
    closeConfirmSubtitle:
      'สถานะจะถูกปิดที่ราคาตลาดและผลลัพธ์จะถือเป็นที่สิ้นสุด การกระทำนี้ย้อนกลับไม่ได้',
  },

  chart: {
    title: 'กราฟ',
    interval: 'ช่วงเวลา',
    chartType: 'ประเภทกราฟ',
    candles: 'แท่งเทียน',
    line: 'เส้น',
    high24h: 'สูงสุด 24 ชม.',
    low24h: 'ต่ำสุด 24 ชม.',
    volume24h: 'ปริมาณ 24 ชม.',
    empty: 'กราฟจะปรากฏเมื่อคู่เทรดนี้มีข้อมูล',
    failed: 'โหลดกราฟไม่สำเร็จ',
    unknownPair: 'ไม่มีคู่เทรดนี้ — เลือกคู่เทรดได้ในแท็บตลาด',
    toMarkets: 'ไปที่ตลาด',
  },

  security: {
    title: 'ความปลอดภัย',
    subtitle: 'การยืนยันขั้นที่สองช่วยปกป้องการเข้าสู่ระบบและการถอนเงิน',
    statusOn: 'เปิด',
    statusOff: 'ปิด',
    explain:
      'ระบบจะขอรหัสจากแอปยืนยันตัวตนทุกครั้งที่เข้าสู่ระบบและทุกคำขอถอนเงิน หากไม่เปิดใช้ บัญชีจะได้รับการปกป้องด้วยรหัสผ่านเพียงอย่างเดียว',
    setup: 'ตั้งค่าแอปยืนยันตัวตน',
    step1: 'สแกนรหัสด้วย Google Authenticator, 1Password หรือ Aegis',
    secretLabel: 'หรือกรอกรหัสลับด้วยตนเอง',
    step2: 'กรอกรหัสจากแอป',
    enable: 'เปิดใช้งาน',
    enabled: 'เปิดการยืนยันสองขั้นตอนแล้ว',
    disableTitle: 'ปิดการยืนยันสองขั้นตอน',
    disableHint: 'กรอกรหัสปัจจุบันเพื่อยืนยันว่าโทรศัพท์เป็นของคุณ',
    disable: 'ปิดใช้งาน',
    disabled: 'ปิดการยืนยันสองขั้นตอนแล้ว',
    codeInvalid: 'รหัสไม่ถูกต้อง โปรดตรวจเวลาบนโทรศัพท์แล้วลองรหัสใหม่',
    failed: 'เปลี่ยนการตั้งค่าความปลอดภัยไม่สำเร็จ',
    withdrawalCode: 'รหัสยืนยัน',
    withdrawalCodeHint: 'การถอนเงินได้รับการปกป้องด้วยการยืนยันขั้นที่สอง: กรอกรหัสจากแอป',
    recoveryTitle: 'รหัสกู้คืน',
    recoveryIntro: 'เก็บรหัสเหล่านี้ไว้ในที่ปลอดภัย แต่ละรหัสใช้เข้าสู่ระบบหรือถอนเงินได้หนึ่งครั้งเมื่อไม่มีโทรศัพท์อยู่กับตัว ระบบจะไม่แสดงรหัสเหล่านี้อีก',
    recoveryCopy: 'คัดลอกรหัส',
    recoveryCopied: 'คัดลอกรหัสแล้ว',
    recoverySaved: 'ฉันบันทึกรหัสไว้แล้ว',
    recoveryLeft: (count: number): string => `รหัสกู้คืนที่เหลือ: ${count}`,
    recoveryRegenerate: 'ออกรหัสกู้คืนชุดใหม่',
    recoveryRegenerateHint: 'รหัสชุดเดิมจะใช้ไม่ได้ กรอกรหัสจากแอป',
    recoveryRegenerated: 'ออกรหัสกู้คืนชุดใหม่แล้ว',
    codeOrRecovery: 'ตัวเลขหกหลักจากแอป หรือรหัสกู้คืน',
  },

  addressBook: {
    title: 'ที่อยู่สำหรับถอนเงิน',
    hint: 'ที่อยู่ใหม่จะเริ่มใช้ได้หลังคุณเปิดลิงก์จากอีเมล เพื่อไม่ให้ผู้ที่ขโมยเซสชันส่งเงินไปยังที่อยู่ของตนเองได้',
    add: 'เพิ่มที่อยู่',
    address: 'ที่อยู่',
    label: 'ชื่อกำกับ',
    labelPlaceholder: 'ตัวอย่างเช่น Ledger',
    send: 'ส่งอีเมลยืนยัน',
    saved: 'ที่อยู่ที่บันทึกไว้',
    empty: 'ยังไม่มีที่อยู่',
    emptyBody: 'เพิ่มที่อยู่ที่คุณใช้ถอนเงิน เมื่อยืนยันผ่านอีเมลแล้ว จะเลือกใช้ในแบบฟอร์มถอนได้ในแตะเดียว',
    statusConfirmed: 'ยืนยันแล้ว',
    statusPending: 'รอการยืนยันทางอีเมล',
    resend: 'ส่งใหม่',
    resendLink: 'ส่งอีเมลยืนยันอีกครั้ง',
    remove: 'ลบที่อยู่',
    removeTitle: 'ลบที่อยู่นี้หรือไม่?',
    removeHint: 'การถอนไปยังที่อยู่นี้จะหยุดลงจนกว่าคุณจะเพิ่มและยืนยันใหม่อีกครั้ง',
    linkSent: 'ส่งอีเมลแล้ว — เปิดลิงก์เพื่อยืนยันที่อยู่',
    alreadyConfirmed: 'ที่อยู่นี้ได้รับการยืนยันแล้ว',
    invalid: 'ที่อยู่ไม่ตรงกับเครือข่ายที่เลือก',
    confirmed: (short: string): string => `ยืนยันที่อยู่ ${short} แล้ว`,
    confirmFailed: 'ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว',
  },

  sessions: {
    title: 'เซสชันและอุปกรณ์',
    subtitle: 'บัญชีของคุณเข้าสู่ระบบอยู่ที่ใดบ้าง',
    current: 'อุปกรณ์นี้',
    started: 'เข้าสู่ระบบเมื่อ',
    lastSeen: 'ใช้งานล่าสุด',
    end: 'จบเซสชัน',
    endOthers: 'จบเซสชันอื่นทั้งหมด',
    ended: 'จบเซสชันแล้ว',
    endedOthers: (count: number): string => `จบเซสชันแล้ว: ${count}`,
    empty: 'ไม่มีเซสชันอื่นที่ใช้งานอยู่',
    failed: 'โหลดเซสชันไม่สำเร็จ',
    hint: 'เซสชันที่จบแล้วจะหมดสิทธิ์เข้าถึงภายในสิบห้านาที ซึ่งเป็นอายุของคีย์เข้าถึงที่ออกให้',
  },

  profile: {
    title: 'โปรไฟล์',
    accountId: 'รหัสบัญชี',
    idCopied: 'คัดลอกรหัสแล้ว',
    loadFailed: 'โหลดโปรไฟล์ของคุณไม่สำเร็จ',
    partnerProgram: 'โปรแกรมพันธมิตร',
    partnerProgramHint: 'ทีม ระดับ และรางวัล',

    verification: 'การยืนยันตัวตน',
    verificationNone: 'ยังไม่เริ่ม',
    verificationPending: 'กำลังตรวจสอบ',
    verificationInReview: 'กำลังตรวจสอบ',
    verificationApproved: 'ยืนยันแล้ว',
    verificationRejected: 'ถูกปฏิเสธ',
    verificationResubmit: 'ต้องใช้เอกสารใหม่',
    verificationCta: 'เริ่ม',
    verificationCtaAgain: 'ส่งใหม่อีกครั้ง',
    verificationPrompt: 'อัปโหลดหนังสือเดินทางของคุณ — ใช้เวลาสองนาที',
    verificationPendingHint: 'โดยปกติเจ้าหน้าที่จะตรวจเอกสารภายในหนึ่งวัน',
    verificationRejectedHint: 'เจ้าหน้าที่ไม่รับเอกสารชุดนี้ คุณสามารถยื่นใหม่ได้',
    verificationOpen: 'เปิดใบคำขอ',

    withdrawalAddressesHint: 'สมุดที่อยู่ ยืนยันผ่านอีเมล',
    withdrawalAddresses: 'ที่อยู่สำหรับถอนเงิน',
    addressesSubtitle: 'ที่อยู่ที่คุณเคยถอนไปแล้ว',
    addressesEmpty: 'คุณยังไม่เคยถอนเงิน ที่อยู่จากคำขอแรกของคุณจะปรากฏที่นี่',
    addressesFailed: 'โหลดรายการที่อยู่ไม่สำเร็จ',
    addressCopied: 'คัดลอกที่อยู่แล้ว',

    security: 'ความปลอดภัย',
    accountAccess: 'อีเมลและรหัสผ่าน',
    sessions: 'เซสชันและอุปกรณ์',
    sessionsHint: 'คุณเข้าสู่ระบบอยู่ที่ใดบ้าง',
    securityOn: 'เปิดการยืนยันสองขั้นตอนแล้ว',
    securityOff: 'ตั้งค่าการยืนยันสองขั้นตอน',
    securitySubtitle: 'การเข้าถึงบัญชี',
    securityEmailVerified: 'ยืนยันอีเมลแล้ว',
    securityEmailUnverified: 'ยังไม่ได้ยืนยันอีเมล',
    securityPassword: 'เปลี่ยนรหัสผ่าน',
    securityPasswordHint: 'เราจะส่งลิงก์สำหรับตั้งรหัสผ่านใหม่ไปทางอีเมล',

    notifications: 'การแจ้งเตือน',
    pushOn: 'เปิดพุช',
    pushOff: 'ปิดพุช',
    pushToggle: 'การแจ้งเตือนแบบพุช',
    pushUnsupported: 'เบราว์เซอร์นี้ไม่รองรับการแจ้งเตือนแบบพุช',
    pushDenied: 'การแจ้งเตือนถูกบล็อกในการตั้งค่าเบราว์เซอร์ของคุณ',
    pushUnavailable: 'ยังไม่ได้ตั้งค่าการแจ้งเตือนแบบพุชบนเซิร์ฟเวอร์',

    pushFailed: 'เปิดการแจ้งเตือนแบบพุชไม่สำเร็จ',

    theme: 'ธีม',
    themeHint: 'สีดำคือสีของแบรนด์และช่วยประหยัดแบตเตอรี่บนจอ OLED ส่วนธีมสว่างเหมาะกับกลางแดด ตัวเลือก «ระบบ» จะตามการตั้งค่าของเครื่อง',
    themeNames: { system: 'ระบบ', dark: 'มืด', light: 'สว่าง' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'ตามการตั้งค่าของอุปกรณ์',
    language: 'ภาษา',
    languageHint: 'ภาษาของอินเทอร์เฟซ จำนวนเงินใช้รูปแบบเดียวเสมอ ส่วนวันที่จะตามภาษา',

    support: 'ฝ่ายสนับสนุน',
    supportTitle: 'ติดต่อฝ่ายสนับสนุน',
    supportChoose: 'เลือกวิธีที่สะดวกสำหรับคุณ',
    supportVia: 'Telegram หรืออีเมล',
    supportTelegram: 'ส่งข้อความทาง Telegram',
    supportEmail: 'ส่งอีเมล',
    about: 'เกี่ยวกับ',
    version: 'เวอร์ชัน',

    signOut: 'ออกจากระบบ',
    signOutTitle: 'ออกจากบัญชีนี้หรือไม่?',
    signOutBody:
      'ยอดเงิน สถานะที่เปิดอยู่ และประวัติสัญญาณของคุณจะคงอยู่เหมือนเดิมทุกประการ และจะกลับมาเมื่อคุณเข้าสู่ระบบครั้งถัดไป อุปกรณ์อื่นยังคงเข้าสู่ระบบอยู่',

    kyc: {
      title: 'การยืนยันตัวตน',
      intro:
        'มีเพียงเจ้าหน้าที่ผู้ตรวจสอบเท่านั้นที่เห็นข้อมูลนี้ หมายเลขเอกสารถูกเก็บแบบเข้ารหัสและจะไม่ถูกแสดงเต็มรูปแบบ',
      loadFailed: 'โหลดใบคำขอไม่สำเร็จ',

      document: 'เอกสาร',
      documentType: 'ประเภทเอกสาร',
      typePassport: 'หนังสือเดินทาง',
      typeIdCard: 'บัตรประชาชน',
      typeDriverLicense: 'ใบขับขี่',
      number: 'หมายเลขเอกสาร',
      numberPlaceholder: '4510 123456',
      numberHint: 'ชุดและหมายเลข ตามที่พิมพ์ไว้ทุกประการ',
      country: 'ประเทศที่ออกเอกสาร',
      countryPlaceholder: 'เลือกประเทศ',
      issueDate: 'วันที่ออก',
      expiryDate: 'ใช้ได้ถึง',

      personal: 'ผู้ถือเอกสาร',
      lastName: 'นามสกุล',
      firstName: 'ชื่อ',
      dateOfBirth: 'วันเกิด',

      documents: 'ภาพถ่ายเอกสาร',
      docMain: 'หน้าหลัก',
      docMainHint: 'หน้าที่มีรูปถ่ายและหมายเลข',
      docFront: 'ด้านหน้า',
      docFrontHint: 'ด้านที่มีรูปถ่ายและหมายเลข',
      choose: 'เลือกไฟล์',
      replace: 'เปลี่ยนไฟล์',
      remove: 'ลบ',
      fileHint: (size: string) => `JPG, PNG หรือ PDF ขนาดไม่เกิน ${size} MB`,
      megabytes: 'MB',

      submit: 'ส่งตรวจสอบ',
      submitFailed: 'ส่งใบคำขอไม่สำเร็จ โปรดตรวจสอบข้อมูลแล้วลองใหม่',
      uploadFailed:
        'ข้อมูลส่งผ่านแล้ว แต่ไฟล์ยังไม่ผ่าน โปรดลองแนบไฟล์อีกครั้ง',
      uploadRetry: 'อัปโหลดเอกสาร',
      submitted: 'ส่งใบคำขอแล้ว',
      submittedHint: 'เจ้าหน้าที่จะตรวจเอกสารและอัปเดตสถานะให้',

      review: 'ใบคำขออยู่ระหว่างตรวจสอบ',
      reviewHint: 'ไม่สามารถแก้ไขข้อมูลได้ขณะอยู่ระหว่างการตรวจสอบ',
      approved: 'ยืนยันตัวตนเสร็จสมบูรณ์',
      approvedHint: 'เจ้าหน้าที่ยืนยันเอกสารแล้ว',
      rejected: 'ใบคำขอถูกปฏิเสธ',
      rejectedHint: 'แก้ไขข้อมูลแล้วส่งใบคำขอใหม่อีกครั้ง',
      resubmit: 'ต้องใช้เอกสารใหม่',
      resubmitHint: 'อัปโหลดเอกสารที่ยังไม่หมดอายุแล้วส่งใบคำขอใหม่',
      rejectionReason: 'เหตุผล',
      submittedAt: (date: string) => `ส่งเมื่อ ${date}`,
      attached: 'เอกสารที่แนบมา',
      attachedEmpty: 'ไม่มีไฟล์แนบในใบคำขอนี้',
      startNew: 'ส่งใหม่อีกครั้ง',
      step: (current: number, total: number): string => `ขั้นที่ ${current} จาก ${total}`,

      gate: {

        title: 'ยืนยันตัวตนเพื่อใช้เงินของคุณ',

        body: 'คุณฝากเงินได้ทันที ส่วนการเทรด สัญญาณ การโอนและการถอนจะเปิดใช้เมื่อเอกสารของคุณได้รับอนุมัติ ซึ่งโดยปกติใช้เวลาภายในหนึ่งวัน',

        pending: 'เอกสารของคุณอยู่ระหว่างตรวจสอบ — สิทธิ์การใช้งานจะเปิดเมื่อได้รับอนุมัติ โดยปกติภายในหนึ่งวัน',

        action: 'ยืนยันตัวตน',

      },

      kinds: {
        passport_main: 'หน้าหลัก',
        passport_registration: 'หน้าทะเบียนบ้าน',
        selfie_with_document: 'เซลฟีคู่กับเอกสาร',
        proof_of_address: 'หลักฐานที่อยู่',
      },

      errors: {
        required: 'กรอกข้อมูลนี้',
        number: 'ใช้ได้เฉพาะตัวอักษร ตัวเลข ช่องว่าง และยัติภังค์',
        name: 'ใช้ได้เฉพาะตัวอักษร ช่องว่าง อะพอสทรอฟี และยัติภังค์',
        country: 'เลือกประเทศ',
        date: 'เลือกวันที่',
        issueFuture: 'วันที่ออกเอกสารต้องไม่อยู่ในอนาคต',
        expiryPast: 'เอกสารหมดอายุแล้ว',
        expiryBeforeIssue: 'วันหมดอายุต้องอยู่หลังวันที่ออกเอกสาร',
        age: 'ยืนยันตัวตนได้ตั้งแต่อายุ 18 ปีขึ้นไป',
        birthAfterIssue: 'วันเกิดต้องอยู่ก่อนวันที่ออกเอกสาร',
        fileType: 'รองรับเฉพาะ JPG, PNG หรือ PDF',
        fileSize: (size: string) => `ไฟล์มีขนาดเกิน ${size} MB`,
        documentsRequired: 'แนบภาพถ่ายหรือสแกนของเอกสาร',
        fileMissing: 'เพิ่มไฟล์นี้',
      },
    },
  },

  countries: {
    search: 'ชื่อประเทศหรือรหัสประเทศ',
    popular: 'ยอดนิยม',
    all: 'ทุกประเทศ',
    nothing: 'ไม่พบข้อมูล ลองใช้ชื่อภาษาอังกฤษหรือรหัส เช่น DE',
    found: (count: number): string => `พบประเทศ: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `ปลดล็อกโบนัสโปรโมชัน ${amount} USDT และโอนเข้าบัญชีสปอตแล้ว`,
    promoExpired: 'โบนัสโปรโมชันหมดอายุ: ไม่ได้ทำตามเงื่อนไขภายในเวลาที่กำหนด',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `ผู้ที่คุณเชิญ ${name} ผ่านเงื่อนไขแล้ว: +${signals.text} สัญญาณโบนัส`
        : `ผู้ที่คุณเชิญ ${name} ผ่านเงื่อนไขแล้ว`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} สัญญาณโบนัส` : null,
        promo === null ? null : `โบนัสโปรโมชัน ${promo} USDT ถูกล็อกไว้จนกว่าจะทำตามเงื่อนไข`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'คุณผ่านเงื่อนไขการแนะนำเพื่อนแล้ว' : `ผ่านเงื่อนไขแล้ว: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'เพิกถอนการผ่านเงื่อนไข: มีคำขอถอนเงินภายในระยะเวลาที่ต้องคงเงินไว้ สัญญาณโบนัสและโบนัสโปรโมชันถูกเรียกคืน'
          : 'เพิกถอนการผ่านเงื่อนไข: สัญญาณโบนัสและโบนัสโปรโมชันถูกเรียกคืน'
        : `การผ่านเงื่อนไขของผู้ที่คุณเชิญ ${name} ถูกเพิกถอน: สัญญาณโบนัสที่ได้จากเขาถูกเรียกคืน`,
    referralRewardPaid: (amount: string): string => `จ่ายแล้ว ${amount} USDT ตามโปรแกรมพันธมิตร`,
    referralRewardRejected: (amount: string): string => `รางวัล ${amount} USDT ถูกเจ้าหน้าที่ปฏิเสธ`,
    partnerLevelReached: (level: string, amount: string): string =>
      `ถึงระดับ ${level} แล้ว: โบนัส ${amount} USDT รอการจ่าย`,
    title: 'การแจ้งเตือน',
    settingsTitle: 'ตั้งค่าการแจ้งเตือน',
    settingsHint: 'หมวดที่ปิดเสียงจะยังอยู่ในประวัติแต่จะไม่รบกวนคุณ ไม่มีจุดแจ้งเตือนและไม่มีพุช การแจ้งเตือนแบบพุชทำงานเมื่อเปิดใช้ในโปรไฟล์',
    channelInApp: 'ในแอป',
    channelPush: 'พุช',
    channelTelegram: 'Telegram',
    telegramHint: 'สิ่งที่บอตส่งเข้าแชทของคุณ หมวดที่ปิดไว้ที่นี่จะไม่ถูกส่งไป Telegram แต่ยังคงอยู่ในแอป',
    telegramLinked: (name: string): string => (name === '' ? 'เชื่อมต่อ Telegram แล้ว' : `เชื่อมต่อ Telegram แล้ว: ${name}`),
    telegramLinkedHint: 'หากไม่ใช่คุณ แตะเพื่อยกเลิกการเชื่อมต่อ',
    telegramUnlinked: (name: string): string => (name === '' ? 'ยกเลิกการเชื่อมต่อ Telegram แล้ว' : `ยกเลิกการเชื่อมต่อ Telegram แล้ว: ${name}`),
    telegramUnlinkedHint: 'แชทนี้ถูกเชื่อมต่อกับบัญชีอื่น',
    categories: { deposit: 'การฝากเงิน', withdrawal: 'การถอนเงิน', copy: 'การคัดลอกการเทรด', trade: 'การเทรดและสถานะ', signal: 'สัญญาณ', promo: 'โบนัสโปรโมชัน', referral: 'โปรแกรมพันธมิตร', kyc: 'การยืนยันตัวตน', system: 'ระบบ' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'ทำเครื่องหมายว่าอ่านแล้วทั้งหมด',
    markAllFailed: 'ทำเครื่องหมายว่าอ่านแล้วไม่สำเร็จ',
    loadFailed: 'โหลดการแจ้งเตือนไม่สำเร็จ',
    empty:
      'ยังไม่มีการแจ้งเตือน เงินฝากที่เข้าบัญชี ผลการพิจารณาคำขอถอน สัญญาณ และผลการเทรดจะปรากฏที่นี่',
    unread: 'ยังไม่ได้อ่าน',
    today: 'วันนี้',
    yesterday: 'เมื่อวาน',
    depositCredited: (amount: string, asset: string) => `เข้าบัญชีแล้ว ${amount} ${asset}`,
    withdrawalApproved: 'อนุมัติการถอนแล้ว',
    withdrawalRejected: 'ปฏิเสธการถอน',
    withdrawalSent: 'ส่งเงินถอนออกสู่เครือข่ายแล้ว',
    marginCall: 'เรียกหลักประกันเพิ่ม',
    positionLiquidated: 'สถานะถูกบังคับปิด',
    copyOpened: (master: string) => `${master} เปิดสถานะใหม่`,
    copyClosed: (master: string) => `${master} ปิดสถานะแล้ว`,
    copyStopLoss: 'จุดตัดขาดทุนของการติดตามทำงาน: ปิดสถานะและคืนเงินเข้าบัญชีสปอตแล้ว',
    copyMasterStopped: 'เทรดเดอร์หยุดแล้ว: สิ้นสุดการคัดลอก และคืนเงินเข้าบัญชีสปอตแล้ว',
    copyReleased: (amount: string) => `คืนเข้าบัญชีสปอต: ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `ส่วนแบ่งกำไรให้เทรดเดอร์: ${commission} USDT จากกำไร ${profit} USDT` : `ส่วนแบ่งกำไรให้ ${master}: ${commission} USDT จากกำไร ${profit} USDT`,
    signalLive: (title: string, until: string) => `สัญญาณ «${title}» เปิดอยู่ถึง ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `สถานะ ${symbol} ถูกบังคับปิดที่ราคา ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: แตะจุดทำกำไรที่ราคา ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: แตะจุดตัดขาดทุนที่ราคา ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `คำสั่งลิมิต ${symbol} จับคู่แล้ว: ${quantity} ที่ราคา ${price}`,
    orderRejected: (symbol: string) => `คำสั่งลิมิต ${symbol} ไม่ได้จับคู่: ระบบปฏิเสธการเทรด`,
    orderRejectedFunds: (symbol: string) => `คำสั่งลิมิต ${symbol} ไม่ได้จับคู่: หลักประกันในบัญชีฟิวเจอร์สไม่พอ`,
    orderRejectedLeverage: (symbol: string) => `คำสั่งลิมิต ${symbol} ไม่ได้จับคู่: เลเวอเรจเกินขีดจำกัดของขนาดนั้น`,
    kycApproved: 'ยืนยันตัวตนผ่านแล้ว',
    kycRejected: 'การยืนยันตัวตนถูกปฏิเสธ',
    kycResubmit: 'การยืนยันตัวตนต้องใช้เอกสารใหม่',
    kycExpiring: (date: string) => `เอกสารของคุณหมดอายุวันที่ ${date} — โปรดต่ออายุการยืนยันตัวตน`,
    kycExpired: 'เอกสารของคุณหมดอายุแล้ว — โปรดอัปโหลดเอกสารที่ยังไม่หมดอายุ',
    signalClosed: (title: string) => `ปิดสัญญาณแล้ว: ${title}`,
    systemAlert: (check: string) => `เหตุขัดข้อง: ${check}`,
    systemRecovered: (check: string) => `กลับมาทำงานปกติ: ${check}`,
    heads: {
      signalLive: 'เปิดสัญญาณแล้ว',
      signalClosed: 'ปิดสัญญาณแล้ว',
      orderFilled: 'คำสั่งลิมิตจับคู่แล้ว',
      orderRejected: 'คำสั่งลิมิตไม่ได้จับคู่',
      takeProfit: 'แตะจุดทำกำไร',
      stopLoss: 'แตะจุดตัดขาดทุน',
      promoUnlocked: 'ปลดล็อกโบนัสโปรโมชันแล้ว',
      promoExpired: 'โบนัสโปรโมชันหมดอายุ',
      referralQualified: 'ผู้ที่เชิญผ่านเงื่อนไขแล้ว',
      referralWelcome: 'ผ่านเงื่อนไขแล้ว',
      referralRevoked: 'เพิกถอนการผ่านเงื่อนไข',
      rewardPaid: 'จ่ายรางวัลแล้ว',
      rewardRejected: 'รางวัลถูกปฏิเสธ',
      partnerLevelReached: 'ระดับพันธมิตรใหม่',
      kycExpiring: 'เอกสารใกล้หมดอายุ',
      kycExpired: 'เอกสารหมดอายุแล้ว',
      systemAlert: 'เหตุขัดข้อง',
      systemRecovered: 'กลับมาทำงานปกติ',
    },
    fallback: {
      deposit: 'เงินฝากเข้าบัญชีแล้ว',
      withdrawal: 'อัปเดตคำขอถอนเงิน',
      trade: 'เหตุการณ์เกี่ยวกับสถานะ',
      copy: 'เหตุการณ์การคัดลอกการเทรด',
      referral: 'เหตุการณ์โปรแกรมพันธมิตร',
      system: 'การแจ้งเตือนของระบบ',
    },
  },

  referrals: {
    title: 'โปรแกรมพันธมิตร',
    rewardKind: {
      fee_rebate: 'ส่วนแบ่งค่าธรรมเนียม',
      signal_grant: 'สัญญาณโบนัส',
      promotional_credit: 'เครดิตโปรโมชัน',
      deposit_bonus: 'ส่วนแบ่งจากเงินฝาก',
      welcome_bonus: 'ส่วนแบ่งต้อนรับ',
      level_bonus: 'โบนัสตามระดับ',
    },
    rewardStatus: {
      pending_review: 'กำลังตรวจสอบ',
      approved: 'อนุมัติแล้ว',
      paid: 'จ่ายแล้ว',
      rejected: 'ถูกปฏิเสธ',
      reversed: 'ถูกเรียกคืน',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'ถูกล็อก',
      unlocked: 'เข้าบัญชีแล้ว',
      expired: 'หมดอายุ',
      revoked: 'ถูกเพิกถอน',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'สมาชิก',
      bronze: 'บรอนซ์',
      silver: 'ซิลเวอร์',
      gold: 'โกลด์',
      platinum: 'แพลทินัม',
      ambassador: 'แอมบาสเดอร์',
    } as Record<string, string>,
    signalUnit: 'สัญญาณ',
    membersFrom: (count: Count): string => `ตั้งแต่ ${count.text} คนขึ้นไป`,
    baseLevel: 'ระดับเริ่มต้น',
    depositFrom: (amount: string): string => `เงินฝากตั้งแต่ ${amount}`,
    noWeeklySignals: 'ไม่มีสัญญาณ',
    perWeek: (count: Count): string => `${count.text} สัญญาณ/สัปดาห์`,
    current: 'ปัจจุบัน',
    copyFailed: 'คัดลอกไม่สำเร็จ',
    inviteText: 'มาร่วม 19CRT ด้วยรหัสแนะนำของฉัน',
    linkCopied: 'คัดลอกลิงก์แล้ว',
    codeCopied: 'คัดลอกรหัสแล้ว',
    loadError: 'โหลดโปรแกรมพันธมิตรไม่สำเร็จ',
    loadErrorHint: 'ตรวจสอบการเชื่อมต่อแล้วลองใหม่',
    retry: 'ลองใหม่',
    disabled: 'โปรแกรมพันธมิตรหยุดชั่วคราว สถิติและรางวัลของคุณยังอยู่ครบ',
    yourCode: 'รหัสของคุณ',
    codeHint: 'เชิญโดยตรง — เงื่อนไขทั้งหมดแสดงให้เห็นก่อนสมัคร',
    openQr: 'เปิดคิวอาร์โค้ดแนะนำเพื่อน',
    qrCode: 'คิวอาร์โค้ด',
    share: 'แชร์',
    copy: 'คัดลอก',
    yourLevel: 'ระดับของคุณ',
    participant: 'สมาชิก',
    allLevels: 'ทุกระดับ',
    progressLabel: 'ความคืบหน้าสู่ระดับถัดไป',
    qualified: (): string => 'ผ่านเงื่อนไข',
    nextLevel: (remaining: Count, level: string): string => `อีก ${remaining.text} คนถึง ${level}`,
    maxLevel: 'ถึงระดับสูงสุดแล้ว',
    invited: 'เชิญแล้ว',
    confirmed: 'ผ่านเงื่อนไข',
    paidUsdt: 'จ่ายแล้ว, USDT',
    signalsReceived: 'สัญญาณที่ได้รับ',
    transparentTitle: 'รางวัลที่โปร่งใส',
    transparentHint:
      'รางวัลคำนวณจากค่าธรรมเนียมการเทรดที่เก็บได้จริงเท่านั้น เงินฝากของผู้ที่คุณเชิญจะไม่ถูกนำไปแบ่งจ่าย',
    rewards: 'รางวัล',
    rewardHistory: 'ประวัติรางวัล',
    underReview: 'กำลังตรวจสอบ',
    emptyRewardsTitle: 'ยังไม่มีรางวัล',
    emptyRewards: 'รางวัลจะปรากฏหลังทีมของคุณมีกิจกรรมที่ผ่านเงื่อนไขครั้งแรก',
    levelsRules: 'ระดับและกติกา',
    levelsRulesHint: 'ทีม ส่วนแบ่งค่าธรรมเนียม และสัญญาณ',
    inviteTitle: 'เชิญเข้าร่วม 19CRT',
    inviteSubtitle: 'รหัสจะผูกกับบัญชีตอนสมัคร',
    copyLink: 'คัดลอกลิงก์',
    levelsTitle: 'ระดับพันธมิตร',
    levelsSubtitle: 'นับเฉพาะสมาชิกที่เชิญโดยตรงและผ่านเงื่อนไขแล้ว',
    loadLevelsError: 'โหลดระดับไม่สำเร็จ',
    levelTeam: 'ระดับและทีม',
    feeSignals: 'ส่วนแบ่งค่าธรรมเนียม · สัญญาณ',
    depositTiersTitle: 'เปอร์เซ็นต์ตามยอดเงินฝาก',
    depositTiersHint: 'ยิ่งเงินฝากครั้งแรกมาก เปอร์เซ็นต์ของทั้งสองฝ่ายก็ยิ่งสูง',
    tierDeposit: 'เงินฝาก',
    tierReferrer: 'คุณได้',
    tierWelcome: 'สมาชิกใหม่ได้',
    tierFrom: (amount: string): string => `ตั้งแต่ ${amount} ขึ้นไป`,
    levelBonus: 'โบนัสตามระดับ',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'กติกาของโปรแกรม',
      qualification: (amount: string, asset: string): string =>
        `เงื่อนไข: เงินฝากที่เข้าบัญชีครั้งแรกอย่างน้อย ${amount} ${asset} ในรายการเดียว`,
      qualificationKyc: 'ต้องยืนยันตัวตนให้เรียบร้อยก่อน',
      welcome: (signals: Count, days: Count): string =>
        `สมาชิกใหม่ได้รับสัญญาณโบนัส ${signals.text} รายการ ใช้ได้ ${days.text} วัน`,
      referrer: (signals: Count, days: Count): string =>
        `พันธมิตรได้รับสัญญาณโบนัส ${signals.text} รายการ ใช้ได้ ${days.text} วัน สำหรับผู้ที่เชิญมาและผ่านเงื่อนไขทุกคน`,
      promo: (amount: string, required: string, days: Count): string =>
        `โบนัสโปรโมชัน ${amount} USDT จะถูกล็อกไว้ และกลายเป็นเงินในบัญชีสปอตเมื่อยอดเทรดสะสมของสมาชิกใหม่ถึง ${required} USDT ภายใน ${days.text} วัน หากไม่ถึงจะหมดอายุ`,
      hold: (days: Count): string =>
        `คำขอถอนเงินใด ๆ ภายใน ${days.text} วันหลังผ่านเงื่อนไข จะทำให้การผ่านเงื่อนไขเป็นโมฆะ พร้อมกับสัญญาณโบนัสและโบนัสโปรโมชัน`,
      feeShare:
        'ส่วนแบ่งค่าธรรมเนียมตามระดับคำนวณจากค่าธรรมเนียมการเทรดที่เก็บได้จริงจากผู้ที่คุณเชิญเท่านั้น รางวัลที่เป็นเงินจะถูกเจ้าหน้าที่ตรวจสอบและจ่ายแยกต่างหาก ไม่มีการโอนเข้ายอดคงเหลือโดยอัตโนมัติ',
      secondLevel: (rate: string): string => `พันธมิตรของพันธมิตรจะได้รับ ${rate} จากค่าธรรมเนียมเดียวกัน`,
      depositTiers:
        'เปอร์เซ็นต์ของผู้เชิญและของสมาชิกใหม่ขึ้นอยู่กับยอดเงินฝากครั้งแรก — ดูตารางในหน้านี้',
      balanceShare: (percent: string): string =>
        `เงินฝากต้องมีมูลค่าไม่น้อยกว่า ${percent}% ของยอดคงเหลือของผู้เชิญ ณ เวลาที่เข้าบัญชีด้วย`,
      topup: (days: Count): string =>
        `เงินฝากเพิ่มภายใน ${days.text} วันนับจากครั้งแรกจะถูกนำมารวมกัน คำนวณใหม่ตามตาราง และจ่ายส่วนต่างให้หนึ่งครั้ง`,
      premiumSignal: (count: string): string =>
        `สิทธิ์สัญญาณพรีเมียมถาวรจะเปิดเมื่อคุณมีผู้ที่เชิญและผ่านเงื่อนไขตั้งแต่ ${count} คนขึ้นไป`,
      antiabuse:
        'หากอุปกรณ์หรือที่อยู่ซ้ำกับพันธมิตรหรือทีมของเขา การผ่านเงื่อนไขจะถูกส่งให้เจ้าหน้าที่ตรวจสอบ',
      paused: 'เจ้าหน้าที่หยุดโปรแกรมไว้ชั่วคราว: ไม่มีการสร้างการผ่านเงื่อนไขหรือรางวัลใหม่',
    },
    disclaimer:
      'รางวัลพันธมิตรไม่ใช่รายได้ที่รับประกัน การสมัคร การฝากเงิน และปริมาณการเทรดเพียงอย่างเดียวไม่ได้ก่อให้เกิดการจ่ายเงิน',
    team: 'ทีม',
    teamStatus: {
      pending: 'รอการฝากเงิน',
      flagged: 'รอเจ้าหน้าที่ตรวจสอบ',
      qualified: 'ผ่านเงื่อนไข',
      revoked: 'ถูกเพิกถอนการผ่านเงื่อนไข',
    },
    joined: (date: string): string => `ตั้งแต่ ${date}`,
    showAll: (count: Count): string => `แสดงทั้งหมด (${count.text})`,
    showLess: 'ย่อลง',
    promoTitle: 'โบนัสโปรโมชัน',
    promoLocked: (amount: string): string => `ล็อกไว้ ${amount} USDT`,
    promoUnlocked: (amount: string): string => `โอน ${amount} USDT เข้าบัญชีสปอตแล้ว`,
    promoExpired: 'หมดอายุแล้ว โบนัสถูกยกเลิก',
    promoRevoked: 'โบนัสถูกเพิกถอน',
    promoCondition: (traded: string, required: string): string => `ยอดเทรด ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `ถึง ${date}`,
    promoHint: 'โบนัสจะกลายเป็นเงินเมื่อยอดเทรดสะสมของคุณถึงเงื่อนไขก่อนหมดเวลา',
  },

  signals: {
    title: 'สัญญาณ',
    tabs: { today: 'วันนี้', history: 'ประวัติ' },
    tabsLabel: 'หมวดของสัญญาณ',
    access: { public: 'สาธารณะ', base: 'พื้นฐาน', premium: 'พรีเมียม', bonus: 'โบนัส', agent: 'พันธมิตร' },
    status: { scheduled: 'ตามกำหนดการ', available: 'พร้อมใช้', executed: 'ดำเนินการแล้ว', expired: 'หมดเวลา', cancelled: 'ยกเลิกแล้ว' },
    side: { buy: 'ซื้อ', sell: 'ขาย' },
    direction: { buy: 'ลอง · ซื้อ', sell: 'ชอร์ต · ขาย' },
    positionSide: { buy: 'ลอง', sell: 'ชอร์ต' },
    localTime: 'เวลาท้องถิ่น',
    localClock: 'เวลาท้องถิ่น',
    timeZone: (offset: string) => `เขตเวลา ${offset}`,
    reason: {
      unavailable: 'ขณะนี้ยังใช้สัญญาณนี้ไม่ได้',
      already: 'คุณใช้สัญญาณนี้ไปแล้ว',
      expired: 'หมดช่วงเวลาที่ใช้สัญญาณได้แล้ว',
      notReleased: 'สัญญาณนี้ยังไม่เปิด',
      cancelled: 'สัญญาณนี้ถูกยกเลิกแล้ว',
      balance: 'ยอดเงินในบัญชีสปอตของคุณไม่เพียงพอ',
      balanceFutures: 'ยอด USDT รวมในบัญชีสปอตและฟิวเจอร์สของคุณไม่เพียงพอ',
      positionConflict: 'คุณมีสถานะที่เปิดอยู่ในตลาดนี้ในทิศทางตรงข้าม — ปิดก่อนจึงจะใช้สัญญาณได้',
      price: 'ขณะนี้ไม่มีราคาสำหรับตลาดนี้ — ลองใหม่ในอีกไม่กี่วินาที',
      access: 'สัญญาณนี้ต้องใช้สิทธิ์การเข้าถึงระดับอื่น',
      kyc: 'ยืนยันตัวตนให้เสร็จก่อน — เมื่อเอกสารได้รับอนุมัติ คุณจะใช้สัญญาณได้',
      minimum: 'สัญญาณนี้ใช้สัดส่วนจากยอดรวมของคุณ และสัดส่วนนั้นต่ำกว่าขั้นต่ำ เติมเงินเพื่อใช้สัญญาณนี้',
      maximum: 'จำนวนเงินเกินขีดสูงสุดของสัญญาณนี้',
      belowMinimum: (amount: string) => `สัญญาณนี้ใช้สัดส่วนจากยอดรวมของคุณ และสัดส่วนนั้นต่ำกว่าขั้นต่ำ ${amount} USDT เติมเงินเพื่อใช้สัญญาณนี้`,
      aboveMaximum: (amount: string) => `จำนวนเงินเกินขีดสูงสุดของสัญญาณนี้ — ได้ไม่เกิน ${amount} USDT`,
      notFound: 'ไม่พบสัญญาณสำหรับรหัสนี้',
      generic: 'ดำเนินการไม่สำเร็จ ตรวจสอบเงื่อนไขแล้วลองใหม่',
      network: 'ติดต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบการเชื่อมต่อแล้วลองใหม่',
    },
    enterCode: 'กรอกรหัส',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'กรอกรหัสจากแชนเนลเพื่อดูทิศทาง ขนาด และเงื่อนไขก่อนยืนยัน', executed: 'ผลลัพธ์และพารามิเตอร์จริงอยู่ในประวัติ', scheduled: (time: string) => `รหัสจะปรากฏในแชนเนลเวลา ${time}` },
    executing: 'กำลังดำเนินการ',
    notExecuted: 'ไม่ได้ดำเนินการ',
    executedLabel: 'ดำเนินการแล้ว',
    loadSignalsError: 'โหลดสัญญาณไม่สำเร็จ',
    noSignalsToday: 'วันนี้ไม่มีสัญญาณ',
    loadErrorHint: 'ตรวจสอบการเชื่อมต่อแล้วลองใหม่',
    retry: 'ลองใหม่',
    signalAvailable: 'มีสัญญาณพร้อมใช้',
    nextSignal: 'สัญญาณถัดไป',
    windowOpenUntil: (time: string) => `เปิดให้ใช้ถึง ${time}`,
    opensAt: (time: string) => `เปิดเวลา ${time}`,
    execute: 'ดำเนินการ',
    doneToday: 'วันนี้ครบแล้ว',
    newBySchedule: 'สัญญาณใหม่จะปรากฏตามกำหนดการ',
    codeOverline: 'รหัสสัญญาณ',
    checkTerms: 'ดูเงื่อนไข',
    codeHint: 'รหัสแต่ละชุดประกาศในแชนเนล Telegram ของเรา — กรอกทันทีที่เห็น',
    openChannel: 'เปิดแชนเนล',
    channelLink: 'แชนเนล Telegram',
    panelTitle: 'เทรดด้วยรหัส',
    dealsTitle: 'การเทรดด้วยรหัส',
    dealsEmpty: 'ยังไม่มีการเทรดด้วยรหัส',
    dealsEmptyHint: 'กรอกรหัสจากแชนเนล — การเทรดและผลลัพธ์จะปรากฏที่นี่',
    codePlaceholder: 'ตัวอย่างเช่น 1QF9ET7RQ',
    codeRequired: 'กรอกรหัสจากแชนเนล',
    codeLabel: 'รหัสสัญญาณ',
    check: 'ตรวจสอบ',
    schedule: 'กำหนดการ',
    todaySignals: 'สัญญาณของวันนี้',
    noSignals: 'ขณะนี้ยังไม่มีสัญญาณ รหัสใหม่จะประกาศตามกำหนดการในแชนเนล Telegram ของเรา',
    riskDisclosure:
      'สัญญาณคือแนวคิดการเทรด ไม่ใช่ผลลัพธ์ที่รับประกัน ก่อนยืนยันคุณจะเห็นทิศทาง จำนวน เลเวอเรจ ค่าธรรมเนียม และเงื่อนไขการปิด',
    loadHistoryError: 'โหลดประวัติสัญญาณไม่สำเร็จ',
    historyEmpty: 'ยังไม่มีประวัติ',
    historyEmptyHint: 'สัญญาณที่ดำเนินการแล้วและพารามิเตอร์จริงจะปรากฏที่นี่',
    toSignals: 'ดูสัญญาณ',
    allExecutions: 'การดำเนินการทั้งหมด',
    signalAccepted: 'รับสัญญาณแล้ว',
    confirmation: 'ยืนยันสัญญาณ',
    sentToTrading: 'ส่งคำสั่งไปยังระบบเทรดแล้ว',
    checkBeforeExecute: 'ตรวจสอบเงื่อนไขก่อนดำเนินการ',
    acceptedHint: 'การแตะซ้ำจะไม่สร้างคำสั่งที่สอง',
    ready: 'เสร็จสิ้น',
    account: 'บัญชี',
    spot: 'สปอต',
    futures: 'ฟิวเจอร์ส',
    perpetualMarket: 'ฟิวเจอร์ส',
    validUntil: 'ใช้ได้ถึง',
    signalName: 'สัญญาณ',
    until: (time: string) => `ถึง ${time}`,
    timeLeft: (countdown: string) => `เหลือ ${countdown}`,
    priceGuide: 'ราคาอ้างอิง',
    entryGuide: 'ราคาเข้าอ้างอิง',
    marketPrice: 'ราคาตลาด',
    stopLoss: 'ตัดขาดทุน',
    takeProfit: 'ทำกำไร',
    autoClose: 'ปิดอัตโนมัติ',
    inDuration: (duration: string) => `ในอีก ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days} วัน` : null, hours > 0 ? `${hours} ชม.` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes} นาที` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'ดำเนินการไปแล้ว',
    executionUnavailable: 'ดำเนินการไม่ได้',
    executionAmount: 'จำนวนที่ใช้ดำเนินการ',
    marginAmount: 'หลักประกัน',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} ของยอดรวมของคุณ · ${total} USDT`,
    estimatedFee: 'ค่าธรรมเนียมโดยประมาณ',
    totalDebit: 'ยอดหักรวม',
    positionSize: 'ขนาดสถานะ',
    willSell: 'จะขาย',
    fundingNote: (amount: string) =>
      `${amount} USDT จะถูกย้ายจากบัญชีสปอตไปยังบัญชีฟิวเจอร์สของคุณสำหรับการเทรดนี้ เมื่อปิดสถานะ จำนวนนั้นพร้อมผลกำไรขาดทุนจะกลับคืนสู่บัญชีสปอตของคุณ`,
    executionRisk:
      'สัญญาณคือแนวคิดการเทรด ไม่ใช่ผลลัพธ์ที่รับประกัน ราคาที่จับคู่จริงอาจต่างจากที่ประมาณไว้ และการเทรดอาจขาดทุนได้ ยืนยันเฉพาะความเสี่ยงที่คุณเข้าใจ',
    executionRiskFutures:
      'สัญญาณคือแนวคิดการเทรด ไม่ใช่ผลลัพธ์ที่รับประกัน ราคาเข้าจริงอาจต่างจากที่ประมาณไว้ และเลเวอเรจขยายทั้งกำไรและขาดทุน จนอาจถูกบังคับปิดสถานะได้ ยืนยันเฉพาะความเสี่ยงที่คุณเข้าใจ',
    retryExecution: 'ลองดำเนินการใหม่',
    outcomeLabel: 'ผลลัพธ์, USDT',
    outcomeOpen: 'เปิดอยู่',
    outcome: { take_profit: 'ปิดที่เป้าหมาย', stop_loss: 'ปิดที่จุดตัดขาดทุน', time: 'ปิดเมื่อครบกำหนด', manual: 'ปิดด้วยตนเอง', liquidation: 'ปิดโดยการบังคับปิด' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'ถึงเป้าหมาย', stop_loss: 'ถึงจุดตัดขาดทุน', time: 'ครบกำหนด', manual: 'ด้วยตนเอง', liquidation: 'ถูกบังคับปิด' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'เปิดอยู่',
    executeFor: 'ดำเนินการได้ถึง',
    openLong: (amount: string) => `เปิดลอง · ${amount} USDT`,
    openShort: (amount: string) => `เปิดชอร์ต · ${amount} USDT`,
    submittedToast: 'ส่งสัญญาณเพื่อดำเนินการแล้ว',
    filledOf: (requested: string, filled: string) =>
      `ขอ ${requested} USDT จับคู่ได้ ${filled} USDT — จำนวนถูกปัดตามขั้นของล็อต`,
    marginRounded: (requested: string) => `ขอ ${requested} USDT — จำนวนถูกปัดตามขั้นของล็อต`,
    longOpened: (margin: string, leverage: number) => `เปิดลองแล้ว: หลักประกัน ${margin} USDT ที่เลเวอเรจ ${leverage}×`,
    shortOpened: (margin: string, leverage: number) => `เปิดชอร์ตแล้ว: หลักประกัน ${margin} USDT ที่เลเวอเรจ ${leverage}×`,
    fundedFromSpot: (amount: string) => `ย้าย ${amount} USDT จากบัญชีสปอตของคุณแล้ว`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'จุดทำกำไร' : null, stopLoss ? 'จุดตัดขาดทุน' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `ปิดอัตโนมัติในอีก ${duration}`
      const byLevel = `ที่${levels.join(duration === null ? ' หรือ ' : ', ')}`
      return duration === null ? `ปิด${byLevel}` : `ปิด${byLevel} หรือในอีก ${duration}`
    },
    openPosition: 'ไปที่สถานะ',
    returnedToSpot: (amount: string) => `คืนเข้าบัญชีสปอต: ${amount} USDT`,
    positionAccepted: 'สถานะเปิดอยู่ในบัญชีฟิวเจอร์สของคุณแล้ว',
  },

  notFound: {
    title: 'ไม่พบหน้านี้',
    message: 'ไม่มีหน้านี้อยู่ — ลิงก์ล้าสมัยหรือที่อยู่พิมพ์ผิด',
    action: 'ไปหน้าแรก',
  },
}
